import DataGrid from 'react-data-grid';
import Select from 'react-select'

import { getOrderWaitConfirmColumns } from '../../components/TableColumns'
import { useState, useContext, useMemo, useCallback } from 'react'
import { 
	OrderStatusDisplayPdAdmin, 
	OrderType, 
	OrderStatusCode,
  EditSapMasterStatusDisplay, 
	Division, 
	DivisionValue, 
	OrderNotes,
	OrderNoteDisplays
} from '../../constants/Enum'
import SyncExpressionContext from '../../contexts/SyncExpressionContext'
import { parseBatchLots } from '../../utilities/Validate'

import DatePicker from 'react-datepicker'
import { FiRefreshCcw } from 'react-icons/fi'

import usePdOrder from '../../hooks/usePdOrder'
import ExportAsButton from "../../components/ExportAsButton";

import { writeFile, utils } from 'xlsx';
import { formatDateObject } from "../../utilities/Format";

const ProductionOrderConfirm = () => {
	const { syncPeriod } = useContext(SyncExpressionContext)

	const [orderType, setOrderType] = useState(OrderType.ALL)
	const [orderStatus, setOrderStatus] = useState(OrderStatusCode.DRAFTED)
  const [editSapMasterStatus, setEditSapMasterStatus] = useState('ALL')
	const [division, setDivision] = useState('ALL')

	const [startDate, setStartDate] = useState(syncPeriod[0]);
	const [endDate, setEndDate] = useState(syncPeriod[1]);
	const [search, setSearch] = useState('');
	const [orderNote, setOrderNote] = useState(OrderNotes.ALL)

	const productionOrders = usePdOrder(
		orderStatus, orderType, startDate, search, division, editSapMasterStatus
	)

	const onTypeChanged = (e) => {
		setOrderType(e.value)
	}

	const onStatusChanged = (e) => {
		setOrderStatus(e.value)
	}

  const onEditSapStatusChanged = (e) => {
		setEditSapMasterStatus(e.value)
	}

	const onDivisionChanged = (e) => {
		setDivision(e.value)
	}

	const onStartDateChanged = (date) => {
		setStartDate(date)
	}

	const onEndDateChanged = (date) => {
		setEndDate(date)
	}

	const onRefreshOrders = () => {
		window.location.reload()
	}

	const onNoteChanged = (e) => {
		setOrderNote(e.value)
	}

	const columns = getOrderWaitConfirmColumns()

	const filterNoteOrders = useMemo(() => {
		if (orderNote === OrderNotes.TRIAL_BATCH) return productionOrders.filter(ord => ord.trialBatch)
		return productionOrders
	}, [ productionOrders, orderNote ])

	const exportFile = useCallback((type: string) => {
    if (type === 'Excel') {
      const wb = utils.book_new();

      const ws = utils.json_to_sheet(filterNoteOrders.map(row => {
				const lots = row.actualBatchNumbers ? parseBatchLots(row.actualBatchNumbers) : parseBatchLots(row.batchNumbers)
				const isEditSts = row.countEditWorkCenter || row.countEditOperation
				return {
					'Order No.': row.id,
					'Order Type': row.orderType,
					'Material Code': row.materialCode,
					'Material Description': row.materialDescription,
					'Lots No.': lots.join('\n'),
					'Order Qty': `${row.orderQty} ${row.materialUnit}`,
					'Actual DueDate': row.actualDueDate ? formatDateObject(new Date(row.actualDueDate), 'dd/MM/yyyy') : '',
					'Edit Status': isEditSts ? 
						`${row.countEditWorkCenter && row.countEditWorkCenter > 0 ? EditSapMasterStatusDisplay.EDIT_WC : ''},${row.countEditOperation && row.countEditOperation > 0 ? EditSapMasterStatusDisplay.EDIT_OPERATION : ''}`
						: '',
					'Trial Batch': row.trialBatch ? row.trialBatch : '-'
				}
			}))
			ws["!cols"] = [
        { wch: 10 },
        { wch: 10 },
        { wch: 15 },
        { wch: 40 },
				{ wch: 15 },
				{ wch: 15 },
				{ wch: 15 },
				{ wch: 15 }
			]
      utils.book_append_sheet(wb, ws, 'Order Wait Confirm');

      const formattedDateStart = formatDateObject(startDate)
      const formattedDateEnd = formatDateObject(endDate)
      const exportDates = formattedDateStart === formattedDateEnd ? 
        formattedDateStart : `${formattedDateStart}_${formattedDateEnd}`
      writeFile(wb, `order_${orderStatus}_${exportDates}.xlsx`);
    }
  }, [ filterNoteOrders, startDate, endDate, orderStatus ])

	return <div className="space-y-3 page-container">
		<div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-x-3 gap-y-2'>
			<h3 className='col-span-2 lg:col-span-5 '>รายการ Order รอการ Confirm</h3>
			<div className='col-span-2 md:col-span-1 lg:col-span-2 pl-0 md:pl-[25%] flex'>
				<button className='gap-2 mr-2 btn-info' onClick={onRefreshOrders}><FiRefreshCcw /> Refresh</button>
				<span className='px-3 py-1.5 bg-gray-200 border border-[#CCCCCC] border-r-0'>#</span>
				<input type="text" placeholder="ค้นหาด้วย PD.Order No." onChange={(e) => setSearch(e.currentTarget.value)}
					className='border border-[#CCCCCC] py-[0.375rem] px-3 w-full'
				></input>
			</div>
			<div>
				<small className='text-muted'>ชนิด Order</small>
				<Select
					value={{ value: orderType, label: orderType }}
					onChange={onTypeChanged}
					options={Object.keys(OrderType).map(typeKey => {
						return { value: OrderType[typeKey], label: OrderType[typeKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
				<small className='text-muted'>สถานะของ Order</small>
				<Select
					value={{ value: orderStatus, label: OrderStatusDisplayPdAdmin[orderStatus] }}
					onChange={onStatusChanged}
					options={Object.keys(OrderStatusDisplayPdAdmin).map(stsKey => {
						return { value: stsKey, label: OrderStatusDisplayPdAdmin[stsKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
				<small className='text-muted'>สถานะการแก้ไขจากหน้างาน</small>
				<Select
					value={{ value: editSapMasterStatus, label: EditSapMasterStatusDisplay[editSapMasterStatus] }}
					onChange={onEditSapStatusChanged}
					options={Object.keys(EditSapMasterStatusDisplay).map(typeKey => {
						return { value: typeKey, label: EditSapMasterStatusDisplay[typeKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
				<small className='text-muted'>Division</small>
				<Select
					value={{ value: division, label: division }}
					onChange={onDivisionChanged}
					options={Object.keys(Division).map(stsKey => {
						return { value: DivisionValue[stsKey] ? DivisionValue[stsKey] : 'ALL', label: Division[stsKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
        <small className='text-muted'>หมายเหตุ</small>
				<Select
					value={{ value: orderNote, label: OrderNoteDisplays[orderNote] }}
					onChange={onNoteChanged}
					options={Object.keys(OrderNoteDisplays).map(stsKey => {
						return { value: stsKey, label: OrderNoteDisplays[stsKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div className='flex flex-col justify-end'>
				<small className='text-muted'>แสดงข้อมูลวันที่</small>
				<DatePicker
					className='input-form-date'
					selected={startDate}
					onChange={onStartDateChanged}
					selectsStart
					startDate={startDate}
					endDate={endDate}
					dateFormat="dd/MM/yyyy"
				/>
			</div>
			<div className='flex flex-col justify-end'>
				<small className='text-muted'>ถึงวันที่</small>
				<DatePicker
					className='input-form-date'
					selected={endDate}
					onChange={onEndDateChanged}
					selectsEnd
					startDate={startDate}
					endDate={endDate}
					minDate={startDate}
					dateFormat="dd/MM/yyyy"
				/>
			</div>
		</div>
		<div className='flex justify-end '>
			<ExportAsButton
				onExportAs={exportFile}
			/>
		</div>
		<DataGrid
			columns={columns}
			rows={filterNoteOrders}
			style={{ height: '75vh' }}
			rowHeight={row => {
        const lots = row.row.actualBatchNumbers ? parseBatchLots(row.row.actualBatchNumbers) : parseBatchLots(row.row.batchNumbers)
        if (lots.length < 2) return 65
        return lots.length * 30
      }}
			defaultColumnOptions={{
				resizable: true
			}}
		/>
	</div>
}

export default ProductionOrderConfirm